import { FavoriteSitter, FavoriteSitterService, Sitter } from 'us-web-services';

export type TrackCallback = (event: string, params?: Record<string, unknown>) => Promise<void> | void;

interface UseSitterSearchCardHandlersArgs {
  userId?: number;
  track: TrackCallback;
  navigateToSitter: (id: number) => void;
  navigateToBook?: (id: number) => void;
  logPrefix: string;
}

interface SitterSearchCardHandlers {
  handleCardClick: (sitter: Sitter) => void;
  handleToggleFavorite: (sitter: Sitter) => Promise<void>;
  handleBook?: (sitter: Sitter) => void;
}

export const useSitterSearchCardHandlers = ({
  userId,
  track,
  navigateToSitter,
  navigateToBook,
  logPrefix,
}: UseSitterSearchCardHandlersArgs): SitterSearchCardHandlers => {
  const setFavorite = async (
    isFavorited: boolean,
    favoriteId: number,
    sitterId: number,
  ): Promise<void> => {
    if (!userId) return;
    const body: FavoriteSitter = { parentId: userId, sitterId };
    if (isFavorited) {
      await FavoriteSitterService.create(body);
    } else {
      await FavoriteSitterService.delete(favoriteId);
    }
  };

  const handleCardClick = (sitter: Sitter): void => {
    void track('clicked-button', { elementId: 'sitter-card', sitterId: sitter.id });
    if (sitter.id != null) navigateToSitter(sitter.id);
  };

  const handleToggleFavorite = async (sitter: Sitter): Promise<void> => {
    if (sitter.id == null) return;
    const isFavorited = sitter.relationship?.bookmarked ?? false;
    const favoriteId = sitter.relationship?.favorite?.id ?? 0;
    try {
      await setFavorite(!isFavorited, favoriteId, sitter.id);
      void track('clicked-button', { elementId: 'favorite', sitterId: sitter.id });
    } catch (err) {
      console.warn(`[${logPrefix}] favorite toggle failed`, err);
    }
  };

  const handleBook = navigateToBook
    ? (sitter: Sitter): void => {
      if (sitter.id == null) return;
      void track('clicked-button', { elementId: 'book-cta', sitterId: sitter.id });
      navigateToBook(sitter.id);
    }
    : undefined;

  return { handleCardClick, handleToggleFavorite, handleBook };
};
